import React from 'react';
import { Form } from 'react-bootstrap';
import Button from '../../../../_components/Button';
import Text from '../../../../_components/Text';
import LoadingInput from '../../../../_components/LoadingInput';
import { useOpenFinance } from './hooks/useOpenFinance';
import OpenFinanceTable from './components/OpenFinanceTable';
import OpenFinanceCategoriesModal from './components/OpenFinanceCategoriesModal';
import AppContext from '../../context';
import { _Input } from '../../../../_components/Input';

const BANK_META = {
    inter: {
        name: 'Banco Inter',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9b/Logo_banco_inter.svg/1200px-Logo_banco_inter.svg.png',
        portal: 'developer.inter.co',
        portalName: 'Portal Desenvolvedor Inter',
        auth: 'mTLS',
        needsCert: true,
        invertLogo: true,
        hint: 'Para conectar ao Banco Inter, você precisa das credenciais OAuth2 e do par de certificados mTLS gerados no Portal Desenvolvedor Inter (developer.inter.co).'
    },
    c6: {
        name: 'C6 Bank (C6 Pay)',
        logo: 'https://logospng.org/wp-content/uploads/c6-bank.png',
        portal: 'developers.c6bank.com.br',
        portalName: 'C6 Developers',
        auth: 'OAuth2',
        needsCert: false,
        invertLogo: false,
        hint: 'Para conectar ao C6 Pay, você precisa apenas do Client ID e Client Secret gerados no C6 Developers (developers.c6bank.com.br). Nenhum certificado é necessário.'
    }
};

export default function OpenFinance() {
    const context = React.useContext(AppContext);
    const [viewMode, setViewMode] = React.useState('all'); // 'in', 'out', 'all'

    const {
        loading,
        transactions,
        previewData,
        setPreviewData,
        companies,
        selectedCompanyId,
        setSelectedCompanyId,
        dateFrom,
        setDateFrom,
        dateTo,
        setDateTo,
        selectedProvider,
        setSelectedProvider,
        error,
        setError,
        fetchTransactions,
        handlePreview,
        handleSync,
        needsSetup,
        setNeedsSetup,
        credentials,
        setCredentials,
        handleSetup,
        bankCategories,
        fetchCategories,
        handleCreateCategory,
        handleDeleteCategory,
        handleUpdateCategory,
        handleDeleteTransaction,
        handleDeleteSetup
    } = useOpenFinance(context);

    const [searchText, setSearchText] = React.useState('');
    const [searchAmount, setSearchAmount] = React.useState('');
    const [showCategoriesModal, setShowCategoriesModal] = React.useState(false);

    const filtered = transactions.filter(item => {
        if (viewMode === 'in' && item.transaction_type !== 'C') return false;
        if (viewMode === 'out' && item.transaction_type !== 'D') return false;
        if (dateFrom && item.date < dateFrom) return false;
        if (dateTo && item.date > dateTo) return false;

        if (searchText && !item.description.toLowerCase().includes(searchText.toLowerCase())) return false;
        if (searchAmount && !String(item.amount).includes(searchAmount)) return false;

        return true;
    });

    const totalEntradas = filtered.filter(t => t.transaction_type === 'C').reduce((s, t) => s + t.amount, 0);
    const totalSaidas = filtered.filter(t => t.transaction_type === 'D').reduce((s, t) => s + t.amount, 0);

    const currentBank = BANK_META[selectedProvider] || BANK_META.inter;
    const logoFilter = currentBank.invertLogo ? 'brightness(0) invert(1)' : undefined;

    return (
        <div className="container-fluid p-4 bg-white" style={{ minHeight: '100vh' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">

                <Button
                    variant="outline-secondary"
                    onClick={() => setNeedsSetup(!needsSetup)}
                    disabled={loading}
                >
                    <i className="fas fa-cog mr-1" /> Configurar API
                </Button>
            </div>

            {/* Seletor de Banco */}
            <div className="card mb-4 shadow-sm border-0" style={{ background: '#f8f9fa' }}>
                <div className="card-body py-4 px-4">
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: 20 }}>
                        <label className="mb-0 font-weight-bold text-dark" style={{ whiteSpace: 'nowrap', fontSize: 15 }}>
                            <i className="fas fa-landmark mr-2 text-primary" />
                            Banco / Instituição:
                        </label>
                        <div style={{ minWidth: 280 }}>
                            <Form.Control as="select" value={selectedProvider} onChange={e => setSelectedProvider(e.target.value)} style={{ fontSize: 14, padding: '8px 12px', height: 'auto' }}>
                                <option value="inter">🏦 Banco Inter — Integração ativa</option>
                                <option value="c6">🏦 C6 Bank — Integração ativa</option>
                                <option value="bb" disabled>🏦 Banco do Brasil — Em breve</option>
                            </Form.Control>
                        </div>
                        {credentials.isConfigured ? (
                            <span className="badge badge-success" style={{ fontSize: 13, padding: '8px 16px', borderRadius: 20 }}>
                                <i className="fas fa-check-circle mr-1" /> Conectado
                            </span>
                        ) : (
                            <span className="badge badge-danger" style={{ fontSize: 13, padding: '8px 16px', borderRadius: 20 }}>
                                <i className="fas fa-times-circle mr-1" /> Ainda não conectado
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Setup Form */}
            {(needsSetup || companies.length === 0) && (
                <div className="card mb-4 border-primary">
                    <div className="card-header bg-primary text-white d-flex align-items-center">
                        <img
                            src={currentBank.logo}
                            alt={currentBank.name}
                            style={{ height: 22, marginRight: 10, filter: logoFilter }}
                            onError={e => { e.target.style.display = 'none'; }}
                        />
                        <h5 className="mb-0">Configuração de Credenciais — {currentBank.name} ({currentBank.auth})</h5>
                    </div>
                    <div className="card-body">
                        <div className="alert alert-info py-2 mb-3" style={{ fontSize: 13 }}>
                            <i className="fas fa-info-circle mr-2" />
                            {currentBank.hint}
                        </div>
                        <div className="row">
                            <div className="col-md-6 mb-3">
                                <label className="form-label">
                                    Client ID <small className="text-muted">({currentBank.name})</small>
                                </label>
                                <Form.Control
                                    type="text"
                                    placeholder="Ex: 0a1b2c3d-xxxx-xxxx-xxxx-..."
                                    value={credentials.clientId}
                                    onChange={e => setCredentials({ ...credentials, clientId: e.target.value })}
                                />
                            </div>
                            <div className="col-md-6 mb-3">
                                <label className="form-label">
                                    Client Secret <small className="text-muted">({currentBank.name})</small>
                                </label>
                                <Form.Control
                                    type="text"
                                    placeholder="Seu client_secret"
                                    value={credentials.clientSecret}
                                    onChange={e => setCredentials({ ...credentials, clientSecret: e.target.value })}
                                />
                            </div>
                            {currentBank.needsCert && (
                                <div className="col-12 row px-0 mx-0">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">
                                            Certificado mTLS <small className="text-muted">(.crt — gerado no {currentBank.portalName})</small>
                                        </label>
                                        <Form.Control
                                            type="file"
                                            accept=".crt,.cer"
                                            onChange={e => setCredentials({ ...credentials, fileCrt: e.target.files[0] })}
                                        />
                                        {credentials.fileCrt && <small className="text-success"><i className="fas fa-check mr-1" />{credentials.fileCrt.name}</small>}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">
                                            Chave Privada mTLS <small className="text-muted">(.key — gerado no {currentBank.portalName})</small>
                                        </label>
                                        <Form.Control
                                            type="file"
                                            accept=".key"
                                            onChange={e => setCredentials({ ...credentials, fileKey: e.target.files[0] })}
                                        />
                                        {credentials.fileKey && <small className="text-success"><i className="fas fa-check mr-1" />{credentials.fileKey.name}</small>}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="d-flex justify-content-between">
                            {credentials.isConfigured ? (
                                <Button variant="outline-danger" onClick={handleDeleteSetup} disabled={loading}>
                                    {loading ? <i className="fas fa-spinner fa-pulse" /> : "Excluir Configuração"}
                                </Button>
                            ) : (
                                <div></div>
                            )}
                            <Button onClick={handleSetup} disabled={loading}>
                                {loading ? <i className="fas fa-spinner fa-pulse" /> : "Salvar e Testar Conexão"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}


            {/* Filtros e Busca */}
            <div className="card mb-4 shadow-sm">
                <div className="card-body">
                    <div className="row align-items-end">
                        {/* Seleção de empresa removida — o sistema opera apenas sobre a branch do usuário logado */}
                        <div className="col-md-3 mb-2">
                            <label className="form-label">Período De</label>
                            <_Input
                                type="date"
                                value={dateFrom}
                                onChange={e => setDateFrom(e.value || (e.target && e.target.value))}
                                disabled={loading}
                            />
                        </div>
                        <div className="col-md-3 mb-2">
                            <label className="form-label">Até</label>
                            <_Input
                                type="date"
                                value={dateTo}
                                onChange={e => setDateTo(e.value || (e.target && e.target.value))}
                                disabled={loading}
                            />
                        </div>
                        <div className="col-md-3 mb-2 d-flex gap-2">
                            <Button onClick={handlePreview} disabled={loading || !selectedCompanyId} className="flex-grow-1">
                                {loading ? <i className="fas fa-spinner fa-pulse" /> : <span><i className="fas fa-search mr-1" /> Buscar no Banco</span>}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Preview Section */}
            {previewData && (
                <div className="card mb-4 border-warning shadow">
                    <div className="card-header bg-warning d-flex justify-content-between align-items-center">
                        <h5 className="mb-0 font-weight-bold"><i className="fas fa-eye mr-2" />Preview do Extrato (Temporal)</h5>
                        <Button size="sm" variant="dark" onClick={handleSync} disabled={loading}>
                            {loading ? <i className="fas fa-spinner fa-pulse" /> : "Confirmar Importação para SQLite"}
                        </Button>
                    </div>
                    <div className="card-body p-0">
                        <OpenFinanceTable data={previewData} isPreview />
                    </div>
                </div>
            )}

            <hr className="my-4" />

            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center">
                    <h4 className="mb-0 font-weight-bold">Transações</h4>
                    <Button
                        variant="outline-info"
                        size="slim"
                        className="ml-3"
                        onClick={() => setShowCategoriesModal(true)}
                    >
                        <i className="fas fa-list-ul mr-1" /> Plano de Contas
                    </Button>
                </div>
                <div className="d-flex align-items-center gap-3">
                    <div className="input-group input-group-sm mr-2" style={{ width: '250px' }}>
                        <div className="input-group-prepend">
                            <span className="input-group-text bg-white border-right-0"><i className="fas fa-search text-muted" /></span>
                        </div>
                        <Form.Control
                            type="text"
                            placeholder="Buscar por descrição..."
                            className="border-left-0 pl-1"
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                        />
                    </div>
                    <div className="input-group input-group-sm mr-3" style={{ width: '150px' }}>
                        <div className="input-group-prepend">
                            <span className="input-group-text bg-white border-right-0">R$</span>
                        </div>
                        <Form.Control
                            type="text"
                            placeholder="Valor..."
                            className="border-left-0 pl-1"
                            value={searchAmount}
                            onChange={e => setSearchAmount(e.target.value)}
                        />
                    </div>
                    <Text className="mr-2 mb-0 font-weight-bold text-uppercase small text-muted">Exibir:</Text>
                    <div className="btn-group shadow-sm">
                        <button
                            className={`btn btn-sm ${viewMode === 'all' ? 'btn-primary' : 'btn-white border'}`}
                            onClick={() => setViewMode('all')}
                        >
                            Tudo
                        </button>
                        <button
                            className={`btn btn-sm ${viewMode === 'out' ? 'btn-primary' : 'btn-white border'}`}
                            onClick={() => setViewMode('out')}
                        >
                            Saídas (A Pagar)
                        </button>
                        <button
                            className={`btn btn-sm ${viewMode === 'in' ? 'btn-primary' : 'btn-white border'}`}
                            onClick={() => setViewMode('in')}
                        >
                            Entradas (A Receber)
                        </button>
                    </div>
                </div>
            </div>

            {/* Totalizadores Contextuais - Premium Style */}
            <OpenFinanceCategoriesModal
                show={showCategoriesModal}
                onHide={() => setShowCategoriesModal(false)}
                categories={bankCategories}
                onCreate={handleCreateCategory}
                onDelete={handleDeleteCategory}
            />

            {!loading && filtered.length > 0 && (
                <div className="row mb-4">
                    {(viewMode === 'in' || viewMode === 'all') && (
                        <div className={viewMode === 'all' ? "col-md-4" : "col-md-12"}>
                            <div className="card border-0 shadow-sm transition-hover" style={{ borderLeft: '4px solid #28a745' }}>
                                <div className="card-body p-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <Text className="text-success font-weight-bold mb-0 small text-uppercase">Total Entradas</Text>
                                            <h3 className="mb-0 font-weight-bold mt-1">R$ {totalEntradas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</h3>
                                        </div>
                                        <div className="bg-success-light p-3 rounded-circle">
                                            <i className="fas fa-arrow-trend-up text-success fa-lg" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {(viewMode === 'out' || viewMode === 'all') && (
                        <div className={viewMode === 'all' ? "col-md-4" : "col-md-12"}>
                            <div className="card border-0 shadow-sm transition-hover" style={{ borderLeft: '4px solid #dc3545' }}>
                                <div className="card-body p-3">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <Text className="text-danger font-weight-bold mb-0 small text-uppercase">Total Saídas</Text>
                                            <h3 className="mb-0 font-weight-bold mt-1">R$ {totalSaidas.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</h3>
                                        </div>
                                        <div className="bg-danger-light p-3 rounded-circle">
                                            <i className="fas fa-arrow-trend-down text-danger fa-lg" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {viewMode === 'all' && (
                        <div className="col-md-4">
                            <div className="card border-0 shadow-sm transition-hover bg-primary text-white">
                                <div className="card-body p-3 text-center">
                                    <Text className="text-white opacity-75 font-weight-bold mb-0 small text-uppercase">Saldo Disponível</Text>
                                    <h3 className="mb-0 font-weight-bold mt-1 text-white">
                                        R$ {(totalEntradas - totalSaidas).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                    </h3>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {error && (
                <div className="alert alert-danger shadow-sm">
                    <i className="fas fa-exclamation-circle mr-2" /> {error}
                    <button className="close" onClick={() => setError(null)}>&times;</button>
                </div>
            )}

            <div className="card shadow-sm">
                <div className="card-body p-0">
                    {loading && !previewData ? (
                        <div className="text-center py-5">
                            <i className="fas fa-spinner fa-pulse fa-3x text-primary mb-3" />
                            <p>Carregando histórico do banco...</p>
                        </div>
                    ) : (
                        <OpenFinanceTable
                            data={filtered}
                            categories={bankCategories}
                            onUpdateCategory={handleUpdateCategory}
                            onDeleteTransaction={handleDeleteTransaction}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}



// WEBPACK FOOTER //
// ./src/views/AppViews/Financial/OpenFinance/OpenFinance.jsx